import type { OptionType } from "components/inputs";

/**
 * Value stored in `SurgicalPricingFormData.mmc` when no Medicare Medical
 * Center applies. The API appends a pseudo-row with `mmc: null` and this name
 * to every zipcode lookup (see `ZipcodeMMCAssociationViewSet`).
 */
export const NA_MMC_VALUE = "N/A";

export type MMCLookupResult = {
  mmc: number | null;
  mmc_name: string;
  mmc_state: string;
};

export type MMCOption = OptionType & {
  value: string;
  name: string;
  state: string;
};

export const buildMMCOptions = (
  results: MMCLookupResult[] | undefined,
): MMCOption[] =>
  (results ?? []).map((result) => ({
    // The N/A row carries the zipcode's state, but "N/A (IA)" reads as if
    // Iowa itself were unavailable, so label it plainly.
    label:
      result.mmc === null
        ? NA_MMC_VALUE
        : `${result.mmc_name} (${result.mmc_state})`,
    value: result.mmc_name,
    name: result.mmc_name,
    state: result.mmc_state,
  }));

/**
 * Which MMC the form should hold after the option list changes: keep the
 * current value while it is still offered (a saved MMC, or a deliberate
 * "N/A"), otherwise fall back to the first option, which is the real MMC
 * when the location has one because the API lists N/A last.
 */
export const resolveMMCSelection = (
  options: MMCOption[],
  current: string,
): string => {
  if (options.length === 0) return current;
  return options.some((option) => option.value === current)
    ? current
    : options[0].value;
};
